import React from 'react';
import { ChevronDown } from 'lucide-react';
import {
  leadCategoryPillClassName,
  leadCategorySwatchClassName,
  leadCategoryTextClassName,
} from '../utils/leadCategoryColors';

export type LeadCategoryOption = {
  key: string;
  label: string;
  color?: string | null;
  sort_order?: number;
};

type LeadCategoryPickerProps = {
  value: string;
  categories: LeadCategoryOption[];
  onChange: (key: string) => void;
  disabled?: boolean;
  className?: string;
  allowEmpty?: boolean;
  size?: 'xs' | 'sm';
  /** `text` = farbiger Klartext; `pill` = Soft-Badge; `dot` = Punkt + neutraler Name (Listenstil). */
  variant?: 'text' | 'pill' | 'dot';
};

export function LeadCategoryPicker({
  value,
  categories,
  onChange,
  disabled = false,
  className = '',
  allowEmpty = true,
  size = 'sm',
  variant = 'text',
}: LeadCategoryPickerProps) {
  const [open, setOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const selected = categories.find((c) => c.key === value);
  const textClass = leadCategoryTextClassName(selected?.color);
  const pillClass = leadCategoryPillClassName(selected?.color);
  const displayLabel = selected?.label ?? (allowEmpty && !value ? '—' : 'Kategorie');

  const sizeClass = size === 'xs' ? 'text-[11px]' : 'text-xs';
  const chevronClass = size === 'xs' ? 'w-3 h-3' : 'w-3.5 h-3.5';
  const dotClass = size === 'xs' ? 'h-1.5 w-1.5' : 'h-2 w-2';

  React.useEffect(() => {
    if (!open) return;
    const onOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [open]);

  const options = [
    ...(allowEmpty ? [{ value: '', label: '—', color: null as string | null }] : []),
    ...categories.map((c) => ({ value: c.key, label: c.label, color: c.color ?? null })),
  ];

  const triggerClass =
    variant === 'pill'
      ? `inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-medium ring-1 ring-inset outline-none focus-visible:ring-2 focus-visible:ring-pipedrive-blue/30 disabled:opacity-50 disabled:cursor-not-allowed ${sizeClass} ${pillClass}`
      : variant === 'dot'
        ? `inline-flex items-center gap-1.5 rounded-md px-1.5 py-1 font-medium text-app-text-muted hover:bg-app-hover outline-none focus-visible:ring-2 focus-visible:ring-pipedrive-blue/30 disabled:opacity-50 disabled:cursor-not-allowed ${sizeClass}`
        : `inline-flex items-center gap-0.5 font-bold bg-transparent border-0 p-0 m-0 outline-none focus:outline-none focus-visible:underline disabled:opacity-50 disabled:cursor-not-allowed ${sizeClass} ${textClass}`;

  return (
    <div ref={containerRef} className={`relative inline-flex ${className}`}>
      <button
        type="button"
        disabled={disabled || categories.length === 0}
        onClick={() => !disabled && setOpen((o) => !o)}
        className={triggerClass}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {variant === 'dot' && selected ? (
          <span
            className={`${dotClass} shrink-0 rounded-full ${leadCategorySwatchClassName(selected.color)}`}
            aria-hidden
          />
        ) : null}
        <span className="truncate max-w-[140px]">{displayLabel}</span>
        <ChevronDown className={`${chevronClass} shrink-0 opacity-50`} aria-hidden />
      </button>
      {open && (
        <div
          role="listbox"
          className="absolute top-full left-0 z-50 mt-1 min-w-[160px] bg-app-surface border border-app-border rounded-lg shadow-lg max-h-60 overflow-y-auto"
        >
          {options.map((opt, index) => {
            const isSelected = opt.value === value;
            return (
              <button
                key={opt.value || '__empty'}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
                className={`flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm text-app-text hover:bg-app-hover ${
                  index > 0 ? 'border-t border-app-border' : ''
                } ${isSelected ? 'bg-app-surface-muted' : ''}`}
              >
                {opt.value === '' ? (
                  <span className={`${dotClass} shrink-0 rounded-full bg-slate-300`} aria-hidden />
                ) : (
                  <span
                    className={`${dotClass} shrink-0 rounded-full ${leadCategorySwatchClassName(opt.color)}`}
                    aria-hidden
                  />
                )}
                <span className="truncate">{opt.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
